import axios from "axios";
import Swal from "sweetalert2";

// =========================================================
// BASE URLS
// =========================================================

const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

export const baseUrl = isLocalhost
    ? "http://127.0.0.1:8000/api/"
    : `${window.location.origin}/api/`;

export const baseimgUrl = isLocalhost
    ? "http://127.0.0.1:8000/"
    : `${window.location.origin}/`;

export const wsBaseUrl = isLocalhost
    ? "ws://127.0.0.1:8000"
    : window.location.protocol === "https:"
        ? `wss://${window.location.host}`
        : `ws://${window.location.host}`;

// =========================================================
// USER DETAILS
// =========================================================

// Always read current localStorage values when calling getters.
export const getUserName = () =>
    localStorage.getItem("username") ||
    localStorage.getItem("user_login") ||
    "";

export const getUserGroup = () =>
    localStorage.getItem("userGroup") ||
    localStorage.getItem("user_group") ||
    "";

export const getUserLocation = () =>
    localStorage.getItem("userLocation") || "";

export const getHospitalCode = () =>
    localStorage.getItem("hospital_code") ||
    localStorage.getItem("hospitalCode") ||
    "";

export const ipAddress = "194.238.17.244";

// Backward-compatible exports.
// Existing imports can continue using these names.
export const userName = getUserName();
export const userGroup = getUserGroup();
export const userLocation = getUserLocation();

// =========================================================
// DATE-TIME FORMATTER
// =========================================================

export const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";

    const date = new Date(dateTimeStr);

    if (Number.isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// =========================================================
// LOCATION HELPERS
// =========================================================

const isValidValue = (value) =>
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "";

const getRequestLocation = (config) => {
    const storedLocation = getUserLocation();

    if (isValidValue(storedLocation)) {
        return String(storedLocation).trim();
    }

    const parameterLocation = config.params?.location;

    if (isValidValue(parameterLocation)) {
        return String(parameterLocation).trim();
    }

    return "";
};

const addLocationToRequestBody = (config, location) => {
    if (!location) return;

    const method = String(config.method || "get").toLowerCase();

    if (!["post", "put", "patch"].includes(method)) {
        return;
    }

    const data = config.data;

    // No body was supplied.
    if (data === undefined || data === null) {
        config.data = { location };
        return;
    }

    // JSON/object body.
    if (
        typeof data === "object" &&
        !Array.isArray(data) &&
        !(data instanceof FormData) &&
        !(data instanceof URLSearchParams) &&
        !(data instanceof Blob) &&
        !(data instanceof ArrayBuffer) &&
        !ArrayBuffer.isView(data)
    ) {
        if (!isValidValue(data.location)) {
            config.data = {
                ...data,
                location,
            };
        }

        return;
    }

    // Multipart/form-data body.
    if (data instanceof FormData) {
        if (!isValidValue(data.get("location"))) {
            data.set("location", location);
        }

        return;
    }

    // URL-encoded body.
    if (data instanceof URLSearchParams) {
        if (!isValidValue(data.get("location"))) {
            data.set("location", location);
        }

        return;
    }

    // JSON serialized as a string.
    if (typeof data === "string") {
        try {
            const parsed = JSON.parse(data);

            if (
                parsed &&
                typeof parsed === "object" &&
                !Array.isArray(parsed) &&
                !isValidValue(parsed.location)
            ) {
                parsed.location = location;
                config.data = JSON.stringify(parsed);
            }
        } catch {
            // Do not modify non-JSON string bodies.
        }
    }
};

// =========================================================
// AXIOS REQUEST INTERCEPTOR
// Authentication + tenant + location
// =========================================================

axios.interceptors.request.use(
    (config) => {
        config.headers = config.headers || {};

        // 1. JWT authentication
        const tokens = localStorage.getItem("authTokens");

        if (tokens) {
            try {
                const parsedTokens = JSON.parse(tokens);
                const accessToken = parsedTokens?.access;

                if (accessToken) {
                    config.headers.Authorization =
                        `Bearer ${accessToken}`;
                }
            } catch (error) {
                console.error(
                    "Unable to read authentication tokens:",
                    error
                );
            }
        }

        // 2. Tenant identification
        const hospitalCode = getHospitalCode();

        if (hospitalCode) {
            config.headers["X-Hospital-Code"] = hospitalCode;
        }

        // 3. User location
        const location = getRequestLocation(config);

        if (location) {
            config.params = {
                ...(config.params || {}),
                location,
            };

            // Populate missing location in submitted form data too.
            addLocationToRequestBody(config, location);
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// =========================================================
// AXIOS RESPONSE INTERCEPTOR
// =========================================================

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const errorResponse = error.response;

        if (errorResponse) {
            const status = errorResponse.status;

            const messages = {
                400: "Bad Request. Please check the submitted form data.",
                401: "Unauthorized.",
                403: "Permission Denied.",
                404: "Requested resource was not found.",
                500: "Internal Server Error.",
            };

            Swal.fire({
                title: `Error ${status}`,
                text: messages[status] || "An error occurred.",
                icon: "error",
                confirmButtonText: "OK",
            });
        }

        return Promise.reject(error);
    }
);

// =========================================================
// EXPORTS
// Preserve the existing imports used throughout the project.
// =========================================================

export default axios;
